import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import StorageIcon from "@mui/icons-material/Storage";
import InventoryIcon from "@mui/icons-material/Inventory";
import CategoryIcon from "@mui/icons-material/Category";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {
  Box,
  Typography,
  Grid,
  Stack,
} from "@mui/material";
import PageLayout from "../../components/layout/PageLayout";
import { DarkModeContext } from "../../context/darkModeContext";
const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF",
    text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97",
    border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE",
    hover: "rgba(55,53,47,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.06)",
    shadowHover: "0 8px 24px rgba(0,0,0,0.10)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B",
    text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B",
    border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52",
    hover: "rgba(255,255,255,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.3)",
    shadowHover: "0 8px 24px rgba(0,0,0,0.4)",
  },
};
interface ModuleCard {
  id: string;
  title: string;
  description: string;
  detail: string;
  icon: React.ReactElement;
  path: string;
  accentColor: string;
}
const MODULES: ModuleCard[] = [
  {
    id: "articulos",
    title: "Artículos",
    description: "Gestión del catálogo de productos",
    detail: "Crea, edita y administra artículos con precios, categorías, embalaje e impuestos. Importación masiva desde Excel.",
    icon: <CategoryIcon />,
    path: "/almacen/articulos",
    accentColor: "#2383E2",
  },
  {
    id: "inventarios",
    title: "Inventarios",
    description: "Control de stock por sucursal",
    detail: "Monitorea el stock disponible en cada sucursal, configura alertas de stock mínimo e importa registros en lote.",
    icon: <InventoryIcon />,
    path: "/almacen/inventarios",
    accentColor: "#0f7b6c",
  },
];
const Almacen = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  return (
    <PageLayout icon={StorageIcon} title="Almacén">
        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 960, mx: "auto" }}>
          {/* Hero header */}
          <Box sx={{ mb: 4 }}>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: p.text, mb: 0.5 }}>
              Gestión de Almacén
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: p.textS, maxWidth: 540 }}>
              Administra el catálogo de productos y el stock disponible en cada sucursal desde un solo lugar.
            </Typography>
          </Box>
          {/* Module cards */}
          <Grid container spacing={2.5}>
            {MODULES.map((mod) => (
              <Grid item xs={12} sm={6} key={mod.id}>
                <Box
                  onClick={() => navigate(mod.path)}
                  sx={{
                    border: `1px solid ${p.border}`,
                    borderRadius: "14px",
                    bgcolor: p.bg,
                    boxShadow: p.shadow,
                    cursor: "pointer",
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: mod.accentColor,
                      boxShadow: p.shadowHover,
                      transform: "translateY(-3px)",
                      "& .card-arrow": { transform: "translateX(4px)", opacity: 1 },
                      "& .card-icon-box": { bgcolor: mod.accentColor, "& svg": { color: "#fff" } },
                    },
                    // Accent strip on hover via pseudo element trick with box-shadow
                  }}
                >
                  {/* Icon */}
                  <Box
                    className="card-icon-box"
                    sx={{
                      width: 48, height: 48, borderRadius: "12px",
                      bgcolor: `${mod.accentColor}14`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      transition: "all 0.2s",
                      "& svg": { fontSize: 24, color: mod.accentColor, transition: "color 0.2s" },
                    }}
                  >
                    {mod.icon}
                  </Box>
                  {/* Text */}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text, mb: 0.4 }}>
                      {mod.title}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: mod.accentColor, mb: 0.8, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {mod.description}
                    </Typography>
                    <Typography sx={{ fontSize: "0.84rem", color: p.textS, lineHeight: 1.6 }}>
                      {mod.detail}
                    </Typography>
                  </Box>
                  {/* Footer */}
                  <Stack
                    direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}
                    sx={{ pt: 1.5, borderTop: `1px solid ${p.border}`, color: mod.accentColor }}
                  >
                    <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>Ir al módulo</Typography>
                    <ArrowForwardIcon
                      className="card-arrow"
                      sx={{ fontSize: 15, opacity: 0.7, transition: "all 0.2s" }}
                    />
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
          {/* Quick info footer */}
          <Box
            sx={{
              mt: 4, p: 2.5, borderRadius: "12px",
              bgcolor: p.bgS, border: `1px solid ${p.border}`,
              display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#2383E2" }} />
              <Typography sx={{ fontSize: "0.78rem", color: p.textS }}>
                <Box component="span" sx={{ fontWeight: 600, color: p.text }}>Artículos</Box>
                {" "}— catálogo centralizado con soporte de importación Excel
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#0f7b6c" }} />
              <Typography sx={{ fontSize: "0.78rem", color: p.textS }}>
                <Box component="span" sx={{ fontWeight: 600, color: p.text }}>Inventarios</Box>
                {" "}— alertas automáticas cuando el stock baja del mínimo
              </Typography>
            </Box>
          </Box>
        </Box>
      </PageLayout>
  );
};
export default Almacen;
